import { LibraryItem } from '@/@types/client-api';
import type { ISearchResultItem } from '@/@types/client-api/models/ISearchResultItem';
import { useLaunch } from '@/hooks/shared/useLaunch';
import { getSegmentFromLibraryItem } from '@/components/ui/Snippet/SegmentList/helpers';

import { SnippetContext } from './Snippets/types';
import type { SnippetSegmentType } from '../Snippet/SegmentList/types';
import {
  isLiveProgram,
  isOnDemandProgram,
  isOnDemandSegment,
  getIsLiveProgramExpired,
  snippetToAnalyticsContext
} from './Snippets/helpers';
import { type SearchableType } from './searchableTypeToSnippetVariant';

type GetSegmentList = {
  resultItem: Pick<
    ISearchResultItem,
    'parentProductLink' | 'parentProductLinkText' | 'variationPk' | 'url'
  > &
    Pick<
      LibraryItem,
      | 'segments'
      | 'pk'
      | 'seeMoreSegments'
      | 'parentProductCode'
      | 'totalSegments'
      | 'liveSegments'
      | 'odProgram'
      | 'isPurchased'
      | 'isParentProgramPurchased'
      | 'watchUrl'
      | 'isOdProgramAvailable'
      | 'masterProgram'
      | 'cleProgressPct'
    > & {
      searchableType?: unknown;
    };
  context: SnippetContext;
};

const SINGLE_SEGMENT_PROGRAM_TYPES = ['OHB', 'SECI - OHB', 'Studio Briefing'];

const getLaunchUrl = (seg: SnippetSegmentType) =>
  seg.isPurchased || seg.isParentProgramPurchased ? seg.watchUrl : undefined;

const useGetSegmentList = ({ resultItem, context }: GetSegmentList) => {
  const {
    parentProductLinkText,
    parentProductLink,
    pk,
    variationPk,
    parentProductCode,
    segments,
    totalSegments = 3,
    searchableType: _searchableType,
    masterProgram,
    isOdProgramAvailable,
    cleProgressPct
  } = resultItem || {};

  const searchableType = _searchableType as unknown as SearchableType;
  const { launch } = useLaunch(snippetToAnalyticsContext(context));

  switch (context) {
    case 'search': {
      if (parentProductLink && parentProductLinkText) {
        return {
          programPk: variationPk || '',
          isProgramSegmentList: false,
          segmentListHeaderTitle: parentProductLinkText,
          segmentLink: parentProductLink,
          featuredSegments: [],
          onLaunchMediaPlayer: null
        };
      }
      break;
    }
    case 'library': {
      if (isOnDemandProgram(searchableType) && segments?.length) {
        const featuredSegments: SnippetSegmentType[] = segments?.map(seg => {
          return getSegmentFromLibraryItem({ ...seg, topicNumber: null });
        });
        return {
          programPk: pk || '',
          isProgramSegmentList: true,
          totalSegments: totalSegments ? totalSegments - featuredSegments.length : 3,
          featuredSegments,
          segmentListHeaderTitle: '',
          onLaunchMediaPlayer: (seg: SnippetSegmentType) =>
            launch({
              code: seg.pk,
              isLiveUnregLaunch: false,
              alreadyPurchasedLaunchUrl: getLaunchUrl(seg)
            })
        };
      } else if (isOnDemandSegment(searchableType) && parentProductCode && parentProductLinkText) {
        const segment = getSegmentFromLibraryItem({ ...resultItem, searchableType: undefined });
        return {
          programPk: parentProductCode,
          totalSegments: totalSegments || 3,
          isProgramSegmentList: true,
          isOnDemandSegment: true,
          featuredSegments: [segment],
          segmentListHeaderTitle: parentProductLinkText ?? '',
          programLink: parentProductLink || '',
          onLaunchMediaPlayer: (seg: SnippetSegmentType) =>
            launch({
              code: seg.pk,
              isLiveUnregLaunch: false,
              alreadyPurchasedLaunchUrl: getLaunchUrl(seg)
            })
        };
      } else if (
        SINGLE_SEGMENT_PROGRAM_TYPES.some(programType => programType === masterProgram?.programType)
      ) {
        const isOnDemand = isOnDemandProgram(searchableType);
        const isLive = isLiveProgram(searchableType);
        const isLiveExpired = getIsLiveProgramExpired(resultItem as unknown as LibraryItem);

        const odCard = isOnDemand
          ? getSegmentFromLibraryItem({ ...resultItem, searchableType: undefined })
          : isLive && isLiveExpired && isOdProgramAvailable && resultItem.odProgram
            ? getSegmentFromLibraryItem({ ...resultItem.odProgram, searchableType: undefined })
            : null;

        const odSegments = {
          programPk: parentProductCode || '',
          featuredSegments: odCard ? [odCard] : [],
          totalSegments: 1,
          isProgramSegmentList: true,
          isSingleSegmentProgram: true,
          segmentListHeaderTitle: parentProductLinkText ?? '',
          programLink: parentProductLink || '',
          onLaunchMediaPlayer: (seg: SnippetSegmentType) =>
            launch({
              code: seg.pk,
              isLiveUnregLaunch: false,
              alreadyPurchasedLaunchUrl: getLaunchUrl(seg)
            })
        };

        if (isOnDemand) {
          return odSegments;
        }

        const liveCard =
          isLive && isLiveExpired && cleProgressPct
            ? getSegmentFromLibraryItem({ ...resultItem, searchableType: undefined })
            : null;

        return {
          segments: odSegments,
          liveSegments: {
            programPk: liveCard?.pk || '',
            featuredSegments: liveCard ? [liveCard] : [],
            totalSegments: liveCard ? 1 : 0,
            isProgramSegmentList: true,
            isSingleSegmentProgram: true,
            segmentListHeaderTitle: '',
            programLink: '',
            onLaunchMediaPlayer: (seg: SnippetSegmentType) =>
              launch({
                code: seg.pk,
                isLiveUnregLaunch: false,
                alreadyPurchasedLaunchUrl: getLaunchUrl(seg)
              })
          }
        };
      } else if (
        isLiveProgram(searchableType) &&
        getIsLiveProgramExpired(resultItem as unknown as LibraryItem)
      ) {
        const { liveSegments = [], odProgram = {}, segments } = resultItem;
        if (liveSegments?.length === 0 && !odProgram) return null;
        // Prefer the top-level item sk for expanded segment requests.
        // This matches the live program identifier shown in the parent card.
        const expandedSegmentsProgramCode = String(resultItem.pk ?? resultItem.odProgram?.pk ?? '');
        return {
          liveSegments: {
            programPk: resultItem.pk || '',
            featuredSegments: (liveSegments || [])
              .filter(({ cleProgressPct = 0 }) => cleProgressPct > 0)
              .map(segment =>
                getSegmentFromLibraryItem({
                  ...segment,
                  pk: segment.pk && segment.pk !== '0' ? segment.pk : pk,
                  url: segment.url || resultItem.url
                })
              )
              .map(seg => ({ ...seg, playable: false })),
            totalSegments: liveSegments?.length,
            isProgramSegmentList: true,
            segmentListHeaderTitle: '',
            programLink: '',
            onLaunchMediaPlayer: (seg: SnippetSegmentType) =>
              launch({
                code: seg.pk,
                isLiveUnregLaunch: false,
                alreadyPurchasedLaunchUrl: getLaunchUrl(seg)
              })
          },
          segments: {
            programPk: expandedSegmentsProgramCode,
            featuredSegments: (segments || []).map(getSegmentFromLibraryItem),
            totalSegments,
            isProgramSegmentList: true,
            segmentListHeaderTitle: '',
            programLink: '',
            onLaunchMediaPlayer: (seg: SnippetSegmentType) =>
              launch({
                code: seg.pk,
                isLiveUnregLaunch: false,
                alreadyPurchasedLaunchUrl: getLaunchUrl(seg)
              })
          }
        };
      }
      break;
    }
    default: {
      return null;
    }
  }

  return null;
};

export default useGetSegmentList;
